import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class PdfLibraryService {
  private pdfjsWorkerSrc: string | null = null;
  private pdfLibInstance: any = null;
  private pdfjsInstance: any = null;
  private pdfLibPromise: Promise<void> | null = null;
  private pdfjsPromise: Promise<void> | null = null;

  // CDN configuration for ngx-extended-pdf-viewer assets
  private pdfViewerAssetsFolder: string | null = null;
  private readonly CDN_URLS = [
    'https://cdn.jsdelivr.net/npm/ngx-extended-pdf-viewer@25.6.4/assets/',
    'https://unpkg.com/ngx-extended-pdf-viewer@25.6.4/assets/'
  ];
  private readonly LOCAL_ASSETS_PATH = '/assets';
  private readonly CDN_TIMEOUT_MS = 3000; // 3 seconds timeout per CDN

  /**
   * Initializes PDF-lib library dynamically when needed
   * @returns Promise that resolves when PDF-lib is ready
   */
  async ensurePdfLibInitialized(): Promise<void> {
    if (this.pdfLibInstance) {
      return Promise.resolve();
    }

    if (this.pdfLibPromise) {
      return this.pdfLibPromise;
    }

    this.pdfLibPromise = this.loadPdfLib();
    return this.pdfLibPromise;
  }

  /**
   * Initializes pdfjs-dist library dynamically when needed
   * @returns Promise that resolves when pdfjs-dist is ready
   */
  async ensurePdfjsInitialized(): Promise<void> {
    if (this.pdfjsInstance) {
      return Promise.resolve();
    }

    if (this.pdfjsPromise) {
      return this.pdfjsPromise;
    }

    this.pdfjsPromise = this.loadPdfjs();
    return this.pdfjsPromise;
  }

  private async loadPdfLib(): Promise<void> {
    try {
      // Dynamically import PDF-lib to avoid loading it in main bundle
      const pdfLib = await import('pdf-lib');
      this.pdfLibInstance = pdfLib;
    } catch (error) {
      console.error('Failed to load PDF-lib library:', error);
      throw error;
    }
  }

  private setPdfjsWorkerSrc(pdfjs: any): void {
    if (!this.pdfjsWorkerSrc) {
      this.pdfjsWorkerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
    }
    if (pdfjs.GlobalWorkerOptions.workerSrc !== this.pdfjsWorkerSrc) {
      pdfjs.GlobalWorkerOptions.workerSrc = this.pdfjsWorkerSrc;
    }
  }

  private async loadPdfjs(): Promise<void> {
    try {
      // Dynamically import pdfjs-dist to avoid loading it in main bundle
      const pdfjs = await import('pdfjs-dist');
      this.setPdfjsWorkerSrc(pdfjs);

      this.pdfjsInstance = pdfjs;
    } catch (error) {
      console.error('Failed to load pdfjs-dist library:', error);
      throw error;
    }
  }

  /**
   * Gets the PDF-lib instance after ensuring it's loaded
   * @returns PDF-lib API
   */
  getPdfLib(): any {
    if (!this.pdfLibInstance) {
      throw new Error('PDF-lib not initialized. Call ensurePdfLibInitialized() first.');
    }
    return this.pdfLibInstance;
  }

  /**
   * Gets the pdfjs-dist instance after ensuring it's loaded
   * @returns pdfjs-dist API
   */
  getPdfjs(): any {
    if (!this.pdfjsInstance) {
      throw new Error('pdfjs-dist not initialized. Call ensurePdfjsInitialized() first.');
    }
    return this.pdfjsInstance;
  }

  /**
   * Creates a new PDF document using PDF-lib
   * @returns PDFDocument instance
   */
  async createPdfDocument(): Promise<any> {
    await this.ensurePdfLibInitialized();
    const PDFDocument = this.getPdfLib().PDFDocument;
    return PDFDocument.create();
  }

  /**
   * Loads a PDF document from bytes using PDF-lib
   * @param pdfBytes PDF file as bytes
   * @returns PDFDocument instance
   */
  async loadPdfDocument(pdfBytes: Uint8Array): Promise<any> {
    await this.ensurePdfLibInitialized();
    const PDFDocument = this.getPdfLib().PDFDocument;
    return PDFDocument.load(pdfBytes);
  }

  /**
   * Gets a PDF document using pdfjs-dist for viewing/parsing
   * @param src PDF source (URL or bytes)
   * @returns PDFDocumentProxy instance
   */
  async getPdfDocument(src: string | Uint8Array): Promise<any> {
    await this.ensurePdfjsInitialized();
    const pdfjs = this.getPdfjs();
    this.setPdfjsWorkerSrc(pdfjs);
    const getDocument = pdfjs.getDocument;

    if (typeof src === 'string') {
      return getDocument({ url: src }).promise;
    }

    return getDocument({ data: src }).promise;
  }

  /**
   * Gets the best available assets folder for ngx-extended-pdf-viewer
   * Tries CDNs first with fallback to local assets
   * @returns Promise<string> The assets folder URL/path to use
   */
  async getPdfViewerAssetsFolder(): Promise<string> {
    // Return cached result if available
    if (this.pdfViewerAssetsFolder) {
      this.pdfViewerAssetsFolder = this.normalizeAssetsFolderPath(this.pdfViewerAssetsFolder);
      return this.pdfViewerAssetsFolder;
    }

    // Try each CDN with timeout
    for (const cdnUrl of this.CDN_URLS) {
      const normalizedCdnUrl = this.normalizeAssetsFolderPath(cdnUrl);
      if (await this.testCdnAvailability(normalizedCdnUrl)) {
        this.pdfViewerAssetsFolder = normalizedCdnUrl;
        //console.log(`PDF Viewer: Using CDN assets from ${normalizedCdnUrl}`);
        return normalizedCdnUrl;
      }
    }

    // Fall back to local assets if all CDNs fail
    this.pdfViewerAssetsFolder = this.normalizeAssetsFolderPath(this.LOCAL_ASSETS_PATH);
    console.warn('PDF Viewer: All CDNs unavailable, using local assets');
    return this.pdfViewerAssetsFolder;
  }

  /**
   * Tests if a CDN is available by attempting to fetch a test resource
   * @param cdnUrl The CDN base URL to test
   * @returns Promise<boolean> True if CDN is available
   */
  private async testCdnAvailability(cdnUrl: string): Promise<boolean> {
    try {
      // Test by fetching a small, essential file (locale/en-US/viewer.ftl is always present)
      const testUrl = `${this.normalizeAssetsFolderPath(cdnUrl)}/locale/en-US/viewer.ftl`;
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.CDN_TIMEOUT_MS);

      const response = await fetch(testUrl, {
        method: 'HEAD', // Use HEAD to minimize data transfer
        signal: controller.signal,
        cache: 'no-cache'
      });

      clearTimeout(timeoutId);
      return response.ok;
    } catch (error) {
      // CDN is unavailable (timeout, network error, etc.)
      return false;
    }
  }

  private normalizeAssetsFolderPath(assetsFolder: string): string {
    return assetsFolder.endsWith('/') ? assetsFolder.slice(0, -1) : assetsFolder;
  }
}
